import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatRupiah, MONTHS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, UsersThree, ArrowRight, CurrencyCircleDollar, MagnifyingGlass, LockKey, FileXls, ArrowsClockwise } from "@phosphor-icons/react";
import { MonthYearPicker } from "@/components/inputs";
import { useAuth } from "@/context/AuthContext";

export default function AdminDashboard() {
  const { user: adminUser } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (adminUser?.email?.toLowerCase() === "admin2@samitragroup.com") {
      navigate("/admin/attendance", { replace: true });
    }
  }, [adminUser]);

  const now = new Date();
  const [closed, setClosed] = useState([]);
  const [period, setPeriod] = useState(() => localStorage.getItem("adminPeriod") || `${now.getMonth() + 1}-${now.getFullYear()}`);
  const [busy, setBusy] = useState(false);
  const [pinOpen, setPinOpen] = useState(null);
  const [pinValue, setPinValue] = useState("");

  const loadClosedPeriods = () => {
    api.get("/payroll/closed-periods").then((res) => setClosed(res.data));
  };
  useEffect(() => { loadClosedPeriods(); }, []);
  const [selMonth, selYear] = period.split("-").map(Number);
  useEffect(() => {
    api.get("/payroll/closed-periods").then((res) => setClosed(res.data)).catch(() => {});
  }, [selMonth, selYear]);
  useEffect(() => {
    api.get(`/employees?month=${selMonth}&year=${selYear}`).then((res) => setEmployees(res.data)).catch(() => {});
  }, [selMonth, selYear]);
  const isClosed = closed.some((c) => c.month === selMonth && c.year === selYear);
  const load = () => api.get(`/employees?month=${selMonth}&year=${selYear}`).then((res) => setEmployees(res.data));

  const periodOptions = [];
  for (let i = 0; i < 12; i++) {
    let m = now.getMonth() + 1 - i, y = now.getFullYear();
    while (m <= 0) { m += 12; y -= 1; }
    periodOptions.push({ key: `${m}-${y}`, m, y });
  }

  const exportExcel = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/reports/excel?month=${selMonth}&year=${selYear}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `laporan-${selMonth}-${selYear}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success("Laporan Excel diunduh");
    } catch (e) {
      toast.error("Gagal mengekspor laporan");
    } finally {
      setBusy(false);
    }
  };

  const closeMonth = async () => {
    setBusy(true);
    try {
      const res = await api.post("/payroll/close", { month: selMonth, year: selYear });
      const emailed = res.data?.emailed;
      toast.success(`Buku bulan ditutup. Gaji periode ini terkunci.${emailed != null ? ` Slip gaji terkirim ke ${emailed} karyawan.` : ""}`);
      loadClosedPeriods();
      load();
    } catch (e) {
      toast.error("Gagal menutup buku");
    } finally {
      setBusy(false);
    }
  };

  const reopenMonth = async (c, pin) => {
    setBusy(true);
    try {
      await api.post("/payroll/reopen", { month: c.month, year: c.year, pin });
      toast.success(`Buku ${MONTHS[c.month - 1]} ${c.year} dibuka kembali.`);
      setPinOpen(null);
      setPinValue("");
      loadClosedPeriods();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal membuka buku");
    } finally {
      setBusy(false);
    }
  };

  const filtered = employees.filter(
    (e) => e.name.toLowerCase().includes(search.toLowerCase()) || e.email.toLowerCase().includes(search.toLowerCase())
  );
  const totalPayroll = employees.reduce((s, e) => s + Number(e.payroll?.net_salary || 0), 0);

  return (
    <AppLayout title="Data Karyawan">
      <div className="max-w-6xl space-y-6 animate-fade-up">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
          <div>
            <p className="text-[14px] text-[#64748B]">Admin Panel <span className="mx-1">›</span> <span className="text-[#006398] font-medium">Manajemen Ketenagakerjaan</span></p>
            <h1 className="skn-h1 text-[28px] md:text-[36px] leading-tight mt-1">Data Karyawan</h1>
            <p className="text-[14px] text-[#64748B] mt-1">Direktori personel, jabatan, dan gaji bersih per periode.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="p-5 bg-white border-[#E2E8F0]/80 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="skn-section-label">Total Karyawan</p>
                <p className="text-[28px] font-bold mt-2 text-[#00364e] tabular-nums" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }} data-testid="stat-total-employees">{employees.length}</p>
              </div>
              <div className="w-11 h-11 rounded-lg bg-[#eff4ff] flex items-center justify-center text-[#00364e]"><UsersThree size={24} weight="bold" /></div>
            </div>
          </Card>
          <Card className="p-5 bg-white border-[#E2E8F0]/80 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="skn-section-label">Total Gaji Bersih / Bulan</p>
                <p className="text-[28px] font-bold mt-2 text-[#00364e] tabular-nums" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }} data-testid="stat-total-payroll">{formatRupiah(totalPayroll)}</p>
              </div>
              <div className="w-11 h-11 rounded-lg bg-[#eff4ff] flex items-center justify-center text-[#059669]"><CurrencyCircleDollar size={24} weight="bold" /></div>
            </div>
          </Card>
        </div>

        <Card className="p-6">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label>Periode Laporan</Label>
              <MonthYearPicker
                month={selMonth}
                year={selYear}
                onChange={(m, y) => {
                  const p = `${m}-${y}`;
                  setPeriod(p);
                  localStorage.setItem("adminPeriod", p);
                }}
                years={Array.from(new Set(periodOptions.map((o) => o.y)))}
                monthTestId="report-period-month"
                yearTestId="report-period-year"
                className="w-64"
              />
            </div>
            <Button variant="outline" className="gap-2" onClick={exportExcel} disabled={busy} data-testid="export-excel-button">
              <FileXls size={18} weight="bold" /> Ekspor Excel
            </Button>
            <Button
              variant="outline"
              className="gap-2"
              disabled={busy}
              onClick={() => { loadClosedPeriods(); load(); toast.success("Data periode diperbarui"); }}
              data-testid="refresh-period-button"
            >
              <ArrowsClockwise size={18} weight="bold" /> Refresh
            </Button>
            {isClosed ? (
              <Button variant="outline" className="gap-2 text-[hsl(var(--success))] border-[hsl(var(--success))]/40" disabled data-testid="period-closed-indicator">
                <LockKey size={18} weight="bold" /> Buku Sudah Ditutup
              </Button>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button className="gap-2" disabled={busy} data-testid="close-month-button">
                    <LockKey size={18} weight="bold" /> Tutup Buku
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Tutup buku {MONTHS[selMonth - 1]} {selYear}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Gaji semua karyawan pada periode ini akan dikunci. Perubahan gaji setelah ini tidak akan mengubah riwayat periode yang sudah ditutup.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Batal</AlertDialogCancel>
                    <AlertDialogAction onClick={closeMonth} data-testid="confirm-close-month">Tutup Buku</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
          {closed.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2 items-center">
              <span className="text-xs text-muted-foreground">Periode ditutup:</span>
              {closed.map((c) => (
                <Dialog key={`${c.month}-${c.year}`}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 rounded-full border-border px-3 text-xs" data-testid={`closed-badge-${c.month}-${c.year}`}>
                      <LockKey size={12} weight="bold" /> {MONTHS[c.month - 1]} {c.year}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-sm">
                    <DialogHeader>
                      <DialogTitle>Buka Kembali Buku {MONTHS[c.month - 1]} {c.year}</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">Masukkan PIN khusus untuk membuka kembali periode ini. Setelah terbuka, data gaji, kasbon, dan reimbursement periode terkait dapat diubah lagi.</p>
                    <div className="space-y-1.5">
                      <Label>PIN Buka Buku</Label>
                      <Input
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        value={pinOpen && pinOpen.month === c.month && pinOpen.year === c.year ? pinValue : ""}
                        onChange={(e) => { setPinOpen(c); setPinValue(e.target.value.replace(/\D/g, "")); }}
                        placeholder="4 digit"
                        data-testid={`reopen-pin-${c.month}-${c.year}`}
                      />
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => { setPinOpen(null); setPinValue(""); }}>Batal</Button>
                      <Button
                        disabled={busy || (pinOpen && pinOpen.month === c.month && pinOpen.year === c.year ? pinValue.length !== 4 : true)}
                        onClick={() => reopenMonth(c, pinValue)}
                        data-testid={`confirm-reopen-${c.month}-${c.year}`}
                      >
                        Buka Kembali
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              ))}
            </div>
          )}
        </Card>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative w-full sm:w-72">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Cari nama atau email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="employee-search-input"
            />
          </div>
          <AddEmployeeDialog open={open} setOpen={setOpen} onDone={load} />
        </div>

        <Card className="overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#eff4ff]">
                <TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Nama</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Jabatan</TableHead>
                <TableHead className="text-right text-[11px] uppercase tracking-wider text-[#64748B]">Gaji Bersih</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12 text-muted-foreground">Tidak ada karyawan.</TableCell>
                </TableRow>
              ) : (
                filtered.map((e, idx) => (
                  <TableRow
                    key={e.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/admin/employee/${e.id}`)}
                    data-testid={`employee-row-${idx}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-[#00364e] text-white flex items-center justify-center text-sm font-bold uppercase flex-shrink-0">
                          {e.name[0]}
                        </div>
                        <div>
                          <p className="font-semibold text-[#0b1c30]">{e.name}</p>
                          <p className="text-xs text-[#64748B]">{e.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{e.position || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{formatRupiah(e.payroll?.net_salary)}</TableCell>
                    <TableCell className="text-right">
                      <ArrowRight size={18} className="text-muted-foreground inline" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </AppLayout>
  );
}

function AddEmployeeDialog({ open, setOpen, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", position: "", password: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error("Lengkapi nama, email, dan password");
      return;
    }
    setSaving(true);
    try {
      await api.post("/employees", form);
      toast.success("Karyawan ditambahkan");
      setForm({ name: "", email: "", position: "", password: "" });
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="add-employee-button">
          <Plus size={18} weight="bold" /> Tambah Karyawan
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Tambah Akun Karyawan</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Nama Lengkap</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-employee-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-employee-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Jabatan</Label>
            <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="new-employee-position" />
          </div>
          <div className="space-y-1.5">
            <Label>Password</Label>
            <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-employee-password" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="submit-employee-button">
            {saving ? "Menyimpan…" : "Simpan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
